import type { BaseUnit, Dimension, Unit } from './types.js';

export class UnitMismatchError extends Error {
  constructor(from: Unit, to: BaseUnit) {
    super(
      `Cannot convert ${from} to ${to}: ${from} is ${DIMENSIONS[from]}, ` +
      `${to} is ${DIMENSIONS[to]}. Fix the seed data rather than coercing.`,
    );
    this.name = 'UnitMismatchError';
  }
}

const DIMENSIONS: Record<Unit, Dimension> = {
  g: 'mass',
  kg: 'mass',
  ml: 'volume',
  l: 'volume',
  each: 'count',
};

/** Multiplier from a unit to its dimension's base unit. */
const TO_BASE: Record<Unit, number> = {
  g: 1,
  kg: 1000,
  ml: 1,
  l: 1000,
  each: 1,
};

export function dimensionOf(unit: Unit): Dimension {
  return DIMENSIONS[unit];
}

/**
 * Convert within a dimension only. Crossing dimensions is a data error, not a
 * runtime edge case — 100g of stock is not 100ml of stock.
 */
export function convert(qty: number, from: Unit, to: BaseUnit): number {
  if (DIMENSIONS[from] !== DIMENSIONS[to]) throw new UnitMismatchError(from, to);
  return round2((qty * TO_BASE[from]) / TO_BASE[to]);
}

/**
 * Two decimal places. Guards every boundary against IEEE754 drift.
 *
 * Rounds the magnitude and reapplies the sign, so negatives round the same way
 * positives do. A bare `n + Number.EPSILON` nudges only upward, which corrects
 * the just-below-.5 representation error for positive inputs and silently does
 * the opposite for negative one — `2.675` would round to `2.68` while
 * `-2.675` rounded to `-2.67`.
 */
export function round2(n: number): number {
  const sign = n < 0 ? -1 : 1;
  return (sign * Math.round((Math.abs(n) + Number.EPSILON) * 100)) / 100;
}
